import React from 'react';
import { routeHistory } from '@nackle/biw-web-router';
import {
    HomeView,
    CreateEditObjectView,
    CreateEditCurriculumView,
    NotFoundErrorView,
    CreateEditCategoryView,
    CreateEditNotificationView,
    StatsPage,
    VarsPage,
    CreateEditObjectiveView
} from './pages';
import LearningObjectsListPage from './updated/pages/LearningObjectsListPage/LearningObjectsListPage';
import NotificationsListPage from './updated/pages/NotificationsListPage/NotificationsListPage';
import ObjectivesListPage from './updated/pages/UserStatsPages/ObjectivesListPage';
import ResourcesListPage from './updated/pages/ResourcesListPage/ResourcesListPage';
import ILTPage from './updated/pages/ILTPage/ILTPage';
import CoursesListPage from './updated/pages/CoursesListPage/CoursesListPage';
import TagsListPage from './updated/pages/TagsListPage/TagsListPage';
import CategoriesListPage from './updated/pages/CategoriesListPage/CategoriesListPage';
import CurriculaListPage from './updated/pages/CurriculaListPage/CurriculaListPage';
import CourseEditPage from './updated/pages/CourseEditPage/CourseEditPage';
import AIReportCreatePage from './updated/pages/AIReportCreatePage/AIReportCreatePage';
import AIReportDetailsPage from './updated/pages/AIReportDetailsPage/AIReportDetailsPage';
import CertificatesListPage from './updated/pages/CertificatesListPage/CertificatesListPage';
import ComponentsListPage from './updated/pages/ComponentsListPage/ComponentsListPage';
import ComponentsEditPage from './updated/pages/ComponentsEditPage/ComponentsEditPage';
import EditCertificate from './pages/Certificates/EditCertificate';
import DemoPage from './pages/DemoPage';
import { CreateCertificate } from './pages/Certificates/Create';
import DataExportView from './pages/DataExportPage/DataExportView';
import Settings from './pages/Settings/Settings';
import ReportingNavPage from './updated/pages/Reporting/ReportingNavPage';
import StaticReportingPage from './updated/pages/Reporting/StaticReportingPage';
import WarehouseImportsPage from './updated/pages/Reporting/WarehouseImportsPage';
import AIReportingListPage from './updated/pages/Reporting/AIReportingPage';
import GamificationNav from './updated/pages/UserStatsPages/GamificationNav';
import LearningContentNav from './updated/pages/LearningContentNav/LearningContentNav';
import ContentTaxonomyNav from './updated/pages/ContentTaxonomyNav/ContentTaxonomyNav';
import ViewLeaderboardsPage from './updated/pages/UserStatsPages/ViewLeaderboardsPage';
import CreateEditLeaderboardPage from './updated/pages/UserStatsPages/CreateEditLeaderboardPage';
import CreateEditBadgePage from './updated/pages/UserStatsPages/CreateEditBadgePage';
import ViewBadgesPage from './updated/pages/UserStatsPages/ViewBadgesPage';
import CreateEditResourcePage from './updated/pages/CreateEditResourcePage/CreateEditResourcePage';
import { Redirect } from './components/Redirect/Redirect';
import { FlagGuard } from './components/FlagGuard/FlagGuard';
import { authService } from './auth/authService';
import { flags } from './featureFlags/flags';

export const COMPANY_UUID_REGEX = '/([a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12})?';

const VIEW_PAGE_SEARCH_PARAMS = 'page=1&sort=updatedAt&dir=descend';

export let companyURL = '';

routeHistory.addEventListener( 'changed', ( action, nextRoute, prevRoute ) => {
    const match = nextRoute.pathname.match( COMPANY_UUID_REGEX );

    if ( match ) {
        companyURL = match[ 0 ] !== '/' ? match[ 0 ] : '';
    } else {
        companyURL = '';
    }
} );

/**
 * Wrap a route's component in a feature-flag guard: it renders only when `flag`
 * is enabled, otherwise redirects to a parent landing page. `fallback` is a
 * thunk (not an eager path) so `Routes.*` resolves at navigation time, after the
 * Routes object below has finished being defined.
 */
const guarded = ( Component, flag, fallback ) => ( props ) =>
    <FlagGuard flag={ flag } fallbackPath={ fallback() }>
        <Component { ...props } />
    </FlagGuard>;

export const Routes = {

    Home: {
        data: {
            pattern: COMPANY_UUID_REGEX,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.home',
            render: ( props ) => <HomeView { ...props } />,
        },
        buildPath: () => companyURL || '/',
    },

    Objects: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/learning-objects`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.objects',
            render: ( props ) => <LearningObjectsListPage { ...props } />,
        },
        buildPath: () => {
            return `${ companyURL }/content/learning-objects?${ VIEW_PAGE_SEARCH_PARAMS }`;
        },
    },

    CreateObject: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/learning-objects/create`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.createObject',
            render: ( props ) => <CreateEditObjectView { ...props } />,
        },
        buildPath: () => {
            return `${ companyURL }/content/learning-objects/create`;
        },
    },

    EditObject: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/learning-objects/edit/:objectId`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.editObject',
            render: ( props ) => <CreateEditObjectView { ...props } />,
        },
        buildPath: ( learningObjectId ) => {
            return `${ companyURL }/content/learning-objects/edit/${ learningObjectId }`;
        },
    },

    Courses: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/courses`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.courses',
            render: ( props ) => <CoursesListPage { ...props } />,
        },
        buildPath: () => {
            return `${ companyURL }/content/courses?${ VIEW_PAGE_SEARCH_PARAMS }`;
        },
    },

    LearningContentNav: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content`,
            requireAuthentication: false,
            documentTitle: 'Learning Content',
            render: ( props ) => <LearningContentNav { ...props } />,
        },
        buildPath: () => {
            return `${ companyURL }/content`;
        }
    },

    ContentTaxonomyNav: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/taxonomy`,
            requireAuthentication: false,
            documentTitle: 'Content Taxonomy',
            render: ( props ) => <ContentTaxonomyNav { ...props } />,
        },
        buildPath: () => {
            return `${ companyURL }/taxonomy`;
        }
    },

    Reporting: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/analytics`,
            requireAuthentication: false,
            documentTitle: 'Analytics',
            render: ( props ) => <ReportingNavPage { ...props } />,
        },
        buildPath: () => {
            return `${ companyURL }/analytics`;
        }
    },

    StaticReporting: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/analytics/static-reporting`,
            requireAuthentication: false,
            documentTitle: 'Static Reporting',
            render: guarded( StaticReportingPage, flags.STATIC_REPORTING_ENABLED, () => Routes.Reporting.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/analytics/static-reporting`;
        }
    },

    WarehouseImports: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/analytics/warehouse-imports`,
            requireAuthentication: false,
            documentTitle: 'Warehouse Imports',
            // BIW employees only; anyone else gets the not-found view even via direct URL.
            render: ( props ) => authService.isBiwEmployee()
                ? <WarehouseImportsPage { ...props } />
                : <NotFoundErrorView { ...props } />,
        },
        buildPath: () => {
            return `${ companyURL }/analytics/warehouse-imports`;
        }
    },

    AIReport: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/analytics/ai-reporting`,
            requireAuthentication: false,
            documentTitle: 'AI Reporting',
            render: guarded( AIReportingListPage, flags.AI_REPORTING_ENABLED, () => Routes.Reporting.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/analytics/ai-reporting`;
        }
    },

    AIReportCreatePage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/analytics/ai-reporting/create`,
            requireAuthentication: false,
            documentTitle: 'AI Reporting',
            render: guarded( AIReportCreatePage, flags.AI_REPORTING_ENABLED, () => Routes.Reporting.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/analytics/ai-reporting/create`;
        }
    },

    AIReportDetailsPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/analytics/ai-reporting/details/:promptId`,
            requireAuthentication: false,
            documentTitle: 'AI Reporting',
            render: guarded( AIReportDetailsPage, flags.AI_REPORTING_ENABLED, () => Routes.Reporting.buildPath() ),
        },
        buildPath: ( promptId ) => {
            return `${ companyURL }/analytics/ai-reporting/details/${ promptId }`;
        }
    },

    CreateCourse: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/courses/create`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.createCourse',
            render: ( props ) => <CourseEditPage { ...props } />,
        },
        buildPath: () => {
            return `${ companyURL }/content/courses/create`;
        },
    },

    EditCourse: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/courses/edit/:courseId`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.editCourse',
            render: ( props ) => <CourseEditPage { ...props } />,
        },
        buildPath: ( courseId ) => {
            return `${ companyURL }/content/courses/edit/${ courseId }`;
        },
    },

    Curriculums: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/curricula`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.curriculums',
            render: guarded( CurriculaListPage, flags.CURRICULA, () => Routes.LearningContentNav.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/content/curricula?${ VIEW_PAGE_SEARCH_PARAMS }`;
        },
    },

    CreateCurriculum: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/curricula/create`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.createCourse',
            render: guarded( CreateEditCurriculumView, flags.CURRICULA, () => Routes.LearningContentNav.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/content/curricula/create`;
        },
    },

    EditCurriculum: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/curricula/edit/:curriculumID`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.editCurriculum',
            render: guarded( CreateEditCurriculumView, flags.CURRICULA, () => Routes.LearningContentNav.buildPath() ),
        },
        buildPath: ( curriculumId ) => {
            return `${ companyURL }/content/curricula/edit/${ curriculumId }`;
        },
    },

    ListCategoriesView: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/taxonomy/categories`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.categoryView',
            render: guarded( CategoriesListPage, flags.CATEGORIES, () => Routes.ContentTaxonomyNav.buildPath() ),
        },
        buildPath: () => `${ companyURL }/taxonomy/categories?${ VIEW_PAGE_SEARCH_PARAMS }`
    },

    CreateCategory: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/taxonomy/categories/create`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.createCategory',
            render: guarded( CreateEditCategoryView, flags.CATEGORIES, () => Routes.ContentTaxonomyNav.buildPath() ),
        },
        buildPath: () => `${ companyURL }/taxonomy/categories/create`
    },

    EditCategory: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/taxonomy/categories/edit/:categoryId`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.editCategory',
            render: guarded( CreateEditCategoryView, flags.CATEGORIES, () => Routes.ContentTaxonomyNav.buildPath() ),
        },
        buildPath: ( categoryId ) =>  `${ companyURL }/taxonomy/categories/edit/${ categoryId }`
    },

    ListCertificates: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/certificates`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.listCertificate',
            render: guarded( CertificatesListPage, flags.CERTIFICATES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( ) =>  `${ companyURL }/incentives/certificates?${ VIEW_PAGE_SEARCH_PARAMS }`
    },

    CreateCertificate: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/certificates/create`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.createCertificate',
            render: guarded( CreateCertificate, flags.CERTIFICATES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( ) =>  `${ companyURL }/incentives/certificates/create`
    },

    EditCertificate: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/certificates/edit/:certificateId`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.editCertificate',
            render: guarded( EditCertificate, flags.CERTIFICATES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( certificateId ) =>  `${ companyURL }/incentives/certificates/edit/${ certificateId }`
    },

    DemoPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/demo`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.demo',
            render: ( props ) => <DemoPage { ...props } />
        },
        buildPath: () => {
            return `${ companyURL }/demo`;
        },
    },

    DataExportPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/analytics/data-export`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.dataExport',
            render: guarded( DataExportView, flags.DATA_EXPORT, () => Routes.Reporting.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/analytics/data-export`;
        }
    },

    ResourcesPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/resources`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.resuorces',

            render: guarded( ResourcesListPage, flags.RESOURCES, () => Routes.LearningContentNav.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/content/resources?${ VIEW_PAGE_SEARCH_PARAMS }`;
        }
    },

    NotificationsPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/notifications`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.notifications',

            render: guarded( NotificationsListPage, flags.NOTIFICATIONS, () => Routes.Home.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/notifications?${ VIEW_PAGE_SEARCH_PARAMS }`;
        }
    },

    CreateNotification: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/notifications/create`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.createNotification',
            render: guarded( CreateEditNotificationView, flags.NOTIFICATIONS, () => Routes.Home.buildPath() ),
        },
        buildPath: () => `${ companyURL }/notifications/create`
    },

    EditNotification: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/notifications/edit/:notificationId`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.editNotification',
            render: guarded( CreateEditNotificationView, flags.NOTIFICATIONS, () => Routes.Home.buildPath() ),
        },
        buildPath: ( notificationId ) =>  `${ companyURL }/notifications/edit/${ notificationId }`
    },

    TagsPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/taxonomy/tags`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.tags',

            render: ( props ) => <TagsListPage { ...props } />
        },
        buildPath: () => {
            return `${ companyURL }/taxonomy/tags?${ VIEW_PAGE_SEARCH_PARAMS }`;
        }
    },

    CreateResource: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/resources/create`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.createResource',
            render: guarded( CreateEditResourcePage, flags.RESOURCES, () => Routes.LearningContentNav.buildPath() ),
        },
        buildPath: () => `${ companyURL }/content/resources/create`
    },

    EditResource: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/resources/edit/:resourceId`,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.editResource',
            render: guarded( CreateEditResourcePage, flags.RESOURCES, () => Routes.LearningContentNav.buildPath() ),
        },
        buildPath: ( resourceId ) =>  `${ companyURL }/content/resources/edit/${ resourceId }`
    },

    Settings: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/settings`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.settings',
            render: ( props ) => <Settings { ...props } />,
        },
        buildPath: () => `${ companyURL }/settings`,
    },

    Stats: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/analytics/stats`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.settings',
            render: ( props ) => <StatsPage { ...props } />,
        },
        buildPath: () => `${ companyURL }/analytics/stats`,
    },

    ILTPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/content/ilt`,
            requireAuthentication: true,
            documentTitle: 'Instructor Led Training',

            render: guarded( ILTPage, flags.ILT_ENABLED, () => Routes.LearningContentNav.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/content/ilt`;
        }
    },

    Vars: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/vars`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.settings',
            render: ( props ) => <VarsPage { ...props } />,
        },
        buildPath: () => `${ companyURL }/vars`,
    },

    ComponentsList: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/componentlist`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.settings',
            render: guarded( ComponentsListPage, flags.PAGE_SETUP, () => Routes.Home.buildPath() ),
        },
        buildPath: () => `${ companyURL }/componentlist?${ VIEW_PAGE_SEARCH_PARAMS }`,
    },

    ComponentsListCreate: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/componentlist/create`,
            requireAuthentication: false,
            documentTitle: 'Create Component List',
            render: guarded( ComponentsEditPage, flags.PAGE_SETUP, () => Routes.Home.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/componentlist/create`;
        },
    },

    ComponentsListEdit: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/componentlist/edit/:clsID`,
            requireAuthentication: false,
            documentTitle: 'Edit Component List',
            render: guarded( ComponentsEditPage, flags.PAGE_SETUP, () => Routes.Home.buildPath() ),
        },
        buildPath: ( clsID ) => {
            return `${ companyURL }/componentlist/edit/${ clsID }`;
        },
    },

    GamificationNav: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives`,
            requireAuthentication: false,
            documentTitle: 'Gamification',
            render: ( props ) => <GamificationNav { ...props } />,
        },
        buildPath: ( clsID ) => {
            return `${ companyURL }/incentives`;
        },
    },

    EditLeaderboard: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/leaderboards/edit/:leaderboardId`,
            requireAuthentication: false,
            documentTitle: 'Edit Leaderboard',
            render: guarded( CreateEditLeaderboardPage, flags.LEADERBOARDS, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( leaderboardId ) => {
            return `${ companyURL }/incentives/leaderboards/edit/${ leaderboardId }`;
        },
    },

    CreateLeaderboard: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/leaderboards/create`,
            requireAuthentication: false,
            documentTitle: 'Create Leaderboard',
            render: guarded( CreateEditLeaderboardPage, flags.LEADERBOARDS, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( ) => {
            return `${ companyURL }/incentives/leaderboards/create`;
        },
    },

    ViewLeaderboardsPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/leaderboards`,
            requireAuthentication: false,
            documentTitle: 'Leaderboards',
            render: guarded( ViewLeaderboardsPage, flags.LEADERBOARDS, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( clsID ) => {
            return `${ companyURL }/incentives/leaderboards?${ VIEW_PAGE_SEARCH_PARAMS }`;
        },
    },

    EditBadge: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/badges/edit/:badgeId`,
            requireAuthentication: false,
            documentTitle: 'Edit Badge',
            render: guarded( CreateEditBadgePage, flags.BADGES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( badgeId ) => {
            return `${ companyURL }/incentives/badges/edit/${ badgeId }`;
        },
    },

    CreateBadge: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/badges/create`,
            requireAuthentication: false,
            documentTitle: 'Create Badge',
            render: guarded( CreateEditBadgePage, flags.BADGES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( ) => {
            return `${ companyURL }/incentives/badges/create`;
        },
    },

    ViewBadgesPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/badges`,
            requireAuthentication: false,
            documentTitle: 'Badges',
            render: guarded( ViewBadgesPage, flags.BADGES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( ) => {
            return `${ companyURL }/incentives/badges?${ VIEW_PAGE_SEARCH_PARAMS }`;
        },
    },

    ObjectivesPage: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/objectives`,
            requireAuthentication: true,
            documentTitle: 'application.windowTitle.settings',
            render: guarded( ObjectivesListPage, flags.OBJECTIVES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: () => `${ companyURL }/incentives/objectives?${ VIEW_PAGE_SEARCH_PARAMS }`,
    },

    CreateObjective: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/objectives/create`,
            requireAuthentication: false,
            documentTitle: 'Create Objective',
            render: guarded( CreateEditObjectiveView, flags.OBJECTIVES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: () => {
            return `${ companyURL }/incentives/objectives/create`;
        },
    },

    EditObjective: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/incentives/objectives/edit/:objectiveId`,
            requireAuthentication: false,
            documentTitle: 'Edit Objective',
            render: guarded( CreateEditObjectiveView, flags.OBJECTIVES, () => Routes.GamificationNav.buildPath() ),
        },
        buildPath: ( objectiveId ) => {
            return `${ companyURL }/incentives/objectives/edit/${ objectiveId }`;
        },
    },

    NotAuthorizedError: {
        data: {
            pattern: `${ COMPANY_UUID_REGEX }/unauthorized`,
            regexPattern: /([a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12})?\/unauthorized/,
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.notAuthorizedError',
            render: () => <h1>Not Authorized</h1>,
        },
        buildPath: () => {
            return `${ companyURL }/unauthorized`;
        },
    },

    NotFoundError: {
        data: {
            pattern: '/(.*)', // This pattern allows it to match any possible URL.
            requireAuthentication: false,
            documentTitle: 'application.windowTitle.notFoundError',
            render: ( props ) => <NotFoundErrorView { ...props } />,
        },
        buildPath: () => '/404'
    },

};

/**
 * Legacy URLs that moved during the navigation restructure. Each entry redirects
 * the old path (and everything beneath it) to its new home, preserving trailing
 * segments and query strings. Listed most-specific first so e.g. /reporting/ai
 * is rewritten before the bare /reporting → /analytics rule is considered.
 *
 * `exact: true` skips the subtree rule — used for /reporting, whose children are
 * renamed individually (static-reporting, ai-reporting, warehouse-imports) rather
 * than simply prefixed.
 */
const LEGACY_REDIRECTS = [
    // Learning Content
    { from: '/objects', to: '/content/learning-objects' },
    { from: '/courses', to: '/content/courses' },
    { from: '/curricula', to: '/content/curricula' },
    { from: '/resources', to: '/content/resources' },
    { from: '/ilt', to: '/content/ilt' },
    // Content Taxonomy
    { from: '/categories', to: '/taxonomy/categories' },
    { from: '/tags', to: '/taxonomy/tags' },
    // Incentives
    { from: '/certificates', to: '/incentives/certificates' },
    { from: '/objectives', to: '/incentives/objectives' },
    // Analytics
    { from: '/reporting/static', to: '/analytics/static-reporting' },
    { from: '/reporting/ai', to: '/analytics/ai-reporting' },
    { from: '/reporting/imports', to: '/analytics/warehouse-imports' },
    { from: '/reporting', to: '/analytics', exact: true },
    { from: '/data-export', to: '/analytics/data-export' },
    { from: '/stats', to: '/analytics/stats' },
    // Verb normalization: create pages consistently use /create, not /new.
    { from: '/incentives/leaderboards/new', to: '/incentives/leaderboards/create', exact: true },
    { from: '/incentives/badges/new', to: '/incentives/badges/create', exact: true },
];

const makeRedirect = ( pattern, from, to ) => ( {
    pattern,
    requireAuthentication: false,
    render: () => <Redirect from={ from } to={ to } />,
} );

const redirectRoutes = LEGACY_REDIRECTS.flatMap( ( { from, to, exact } ) => {
    const entries = [ makeRedirect( `${ COMPANY_UUID_REGEX }${ from }`, from, to ) ];

    // Also match anything nested under the old path (e.g. /create, /edit/:id).
    if ( !exact ) {
        entries.push( makeRedirect( `${ COMPANY_UUID_REGEX }${ from }/(.*)`, from, to ) );
    }

    return entries;
} );

/**
 * The list of routes to load into the biw-web-router `routeHistory` object.
 * use mapper so we dont ahve to manually add in each page
 */
export const routeList = [
    ...Object.keys( Routes ).filter( route => Routes[ route ] !== Routes.NotFoundError ).map( route => Routes[ route ].data ),
    // Legacy → new redirects, considered after all real routes but before the
    // catch-all NotFound route below.
    ...redirectRoutes,
    // The pattern of this route allows it to be a match for any URL, but all
    // of the routes listed above this one in the array will be checked and
    // considered before falling back to this route.
    Routes.NotFoundError.data ];
